// src/features/auth/components/ManualEntryOption.tsx
import React, { useState } from 'react';
import { KeyIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

interface ManualEntryOptionProps {
  secretKey?: string;
}

const ManualEntryOption: React.FC<ManualEntryOptionProps> = ({ secretKey }) => {
  const [copied, setCopied] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const handleCopySecret = async () => {
    if (!secretKey) return;
    
    try {
      await navigator.clipboard.writeText(secretKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      
    }
  };

  if (!secretKey) return null;

  return (
    <div className="border border-theme-border dark:border-dark-theme-border rounded-lg p-4">
      <div className="flex items-start space-x-3">
        <KeyIcon className="h-5 w-5 text-theme-text-secondary dark:text-dark-theme-text-secondary mt-0.5 flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <div>
            <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text mb-1">
              Manual Entry Option
            </h5>
            <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
              If you can't scan the QR code, you can manually enter this secret key in your authenticator app
            </p>
          </div>

          {/* Secret Key Display */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-theme-text dark:text-dark-theme-text">
                Secret Key:
              </span>
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="text-xs text-primary-light dark:text-primary-dark hover:underline"
              >
                {showSecret ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showSecret && (
              <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-md p-3">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-theme-text dark:text-dark-theme-text break-all">
                    {secretKey}
                  </code>
                  <button
                    onClick={handleCopySecret}
                    className="ml-2 p-1 text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text transition-colors duration-150"
                    title="Copy secret key"
                  >
                    {copied ? (
                      <CheckIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {copied && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Secret key copied to clipboard!
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Manual Entry Instructions */}
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md p-3">
            <h6 className="text-xs font-medium text-theme-text dark:text-dark-theme-text mb-2">
              Manual Entry Steps:
            </h6>
            <ol className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary space-y-1">
              <li>1. Open your authenticator app</li>
              <li>2. Choose "Add Account" or "Enter Setup Key"</li>
              <li>3. Enter your account name (e.g., "My App")</li>
              <li>4. Paste or type the secret key above</li>
              <li>5. Save the account in your app</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualEntryOption;
